import * as React from 'react'

import { type SortDirection, Spinner, Table } from '@factorialco/deprecated-design-system'
import {
  AssessmentsTableEmployeesDocument,
  AssessmentsTablePoliciesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import AssessmentGroupRow from './_AssessmentGroup'
import UserRow from './_UserRow'

interface Props {
  sortAttribute: string
  direction: SortDirection
  search: string
}

const Body = ({ direction, search }: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { data: employeePolicies } = useQuery(AssessmentsTablePoliciesDocument)
  const policies = employeePolicies?.competencies._policies
  const canSeeEverybody = policies?.assessmentsSeeEverybody.can
  const canSeeOwned = policies?.assessmentsSeeOwned.can
  const canSeeTeam = policies?.assessmentsSeeTeam.can || false
  const canSeeReportees = policies?.assessmentsSeeReportees.can || false

  const hasAllPermissions = canSeeEverybody && canSeeOwned && canSeeTeam && canSeeReportees

  const onlyDirectReportees = canSeeEverybody ? false : canSeeReportees
  const onlyTeam = canSeeEverybody ? false : canSeeTeam

  const {
    data: employees,
    controls: { hasMore, fetchMore },
    loading,
  } = usePaginatedQuery(AssessmentsTableEmployeesDocument, {
    variables: {
      onlyDirectReportees: onlyDirectReportees,
      onlyReportees: onlyTeam,
      sortKey: 'firstName',
      sortOrder: direction,
      search,
    },
    extractConnection: (data) => data.employees.employeesConnection,
  })

  const employeesList =
    employees?.employees.employeesConnection.edges.map((edge) => edge.node) || []

  if (loading) return <Spinner />

  return (
    <Table.BodyWithInfiniteScroll hasMore={hasMore} loadMore={fetchMore} page={0} colSpan={12}>
      {employeesList.length === 0 ? (
        <Table.Blank colSpan={12} text={i18n.t('employees.no_employees_found')} />
      ) : (
        employeesList.map((employee, index) => {
          const isManaged = hasAllPermissions ? true : employee.manager?.id == currentEmployee?.id
          const jobTitle = employee.contractversionsConnection.nodes[0]?.jobTitle

          return (
            <React.Fragment key={`user-${employee.id}-${index}`}>
              <UserRow employee={employee} jobTitle={jobTitle ?? ''} />
              <AssessmentGroupRow employee={employee} isManaged={isManaged} />
            </React.Fragment>
          )
        })
      )}
    </Table.BodyWithInfiniteScroll>
  )
}

export default Body
